import React, { Component } from "react";
import * as Cookies from "es-cookie";
import { Row, Col, Badge } from "react-bootstrap";
import gql from "graphql-tag";
import { withApollo } from "react-apollo";
import * as compose from "lodash.flowright";
import { withRouter } from "react-router-dom";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import TabSection from "./TabSection";
import { PageShimmer } from "./Shimmer";
import { pointTooltip } from "./chartTooltip";
import { examStreamsVar, examKeysVar, examStreamsKey } from './examStreams';

const PREDICTIVE_QUERY = gql`
  query (
    $mobile: String!
    $exam_type: Int
    $exam_streams: [Int], $exam_keys: [String]
    $target: String
    $bucket: String
    $fromDate: String
    $toDate: String
  ) {
    getStudentPredictiveAnalytics(
      mobile: $mobile
      exam_type: $exam_type
      exam_streams: $exam_streams, exam_keys: $exam_keys
      target: $target
      bucket: $bucket
      fromDate: $fromDate
      toDate: $toDate
    ) {
      prediction {
        target
        maxScore
        examsAnalyzed
        currentAvgScore
        trajectorySlope
        trend
        predictedScore
        projectedAccuracy
        platformPercentile
        projectedPercentile
        simulatedAIR
        projectedAIR
        cutoffScore
        meetsCutoff
        message
      }
        percentileStanding {
        examsConsidered
        sectionPercentile
        branchPercentile
        platformPercentile
        sectionSize
        branchSize
      }
      subjectTrend {
        subject_id
        subject
        points {
          period
          label
          avgScore
          accuracy
        }
      }
    }
  }
`;

// JEE / NEET map to the `target` arg (drives maxScore + cutoff).
const TARGETS = [
    { value: "neet", label: "NEET" },
    { value: "jee", label: "JEE" },
];

// Default the target from the student's exam_name cookie (NEET vs JEE), else JEE.
const resolveDefaultTarget = () => {
    const exam = `${Cookies.get("exam_name") || ""}`.toLowerCase();
    if (exam.includes("neet")) return "neet";
    if (exam.includes("jee")) return "jee";
    return "jee";
};

const TREND_VARIANT = { improving: "success", declining: "danger", stable: "secondary" };

const num = (v) => {
    const n = parseFloat(v);
    return isNaN(n) ? 0 : n;
};

// Mobile is the key the predictive query uses (not student id).
const resolveMobile = (student) =>
    Cookies.get("mobile") || student?.contact_no || student?.student_mobile || student?.mobile || null;

class PredictionRankTab extends Component {
    constructor(props) {
        super(props);
        this.state = {
            target: resolveDefaultTarget(),
            examType: 0,
            bucket: "month",
            prediction: null,
            percentileStanding: null,
            subjectTrend: [],
            subjectTrendView: "graph", // "graph" | "table"
            loading: false,
        };
    }

    componentDidMount() {
        this.fetch();
    }

    // Re-fetch when the header exam-stream (Mains/Advanced) changes.
    componentDidUpdate(prevProps) {
        if (examStreamsKey(prevProps.examStreams) !== examStreamsKey(this.props.examStreams)) {
            this.fetch();
        }
    }

    fetch = async () => {
        const mobile = resolveMobile(this.props.student);
        if (!mobile) {
            this.setState({ prediction: null, subjectTrend: [], loading: false });
            return;
        }
        this.setState({ loading: true });
        try {
            const res = await this.props.client.query({
                query: PREDICTIVE_QUERY,
                variables: {
                    mobile: String(mobile),
                    exam_type: Number(this.state.examType) || 0,
                    exam_streams: examStreamsVar(this.props.examStreams),
                    exam_keys: examKeysVar(this.props.examKeys),
                    target: this.state.target,
                    bucket: this.state.bucket,
                    fromDate: null,
                    toDate: null,
                },
                fetchPolicy: "cache-first",
            });
            const data = res?.data?.getStudentPredictiveAnalytics;
            this.setState({
                prediction: data?.prediction || null,
                percentileStanding: data?.percentileStanding || null,
                subjectTrend: data?.subjectTrend || [],
                loading: false,
            });
        } catch (e) {
            console.log(e);
            this.setState({ prediction: null, percentileStanding: null, subjectTrend: [], loading: false });
        }
    };

    setTarget = (target) => {
        if (target === this.state.target) return;
        this.setState({ target }, this.fetch);
    };

    setBucket = (bucket) => {
        if (bucket === this.state.bucket) return;
        this.setState({ bucket }, this.fetch);
    };

    // Monthly / Weekly period toggle only. The exam target (JEE/NEET) is the
    // student's own program, so it's not shown as a selectable control.
    renderControls = () => (
        <div className="d-flex flex-wrap align-items-center" style={{ gap: "10px" }}>
            <div className="sp-segment">
                <button
                    type="button"
                    className={this.state.bucket === "month" ? "active" : ""}
                    onClick={() => this.setBucket("month")}
                >
                    <i className="far fa-calendar-alt" />Monthly
                </button>
                <button
                    type="button"
                    className={this.state.bucket === "week" ? "active" : ""}
                    onClick={() => this.setBucket("week")}
                >
                    <i className="far fa-calendar-week" />Weekly
                </button>
            </div>
        </div>
    );

    renderMetric = (label, value, sub, subColor) => (
        <Col md={4} sm={6} className="mb-3 mb-md-0" style={{ minWidth: 0 }}>
            <div className="text-muted mb-1" style={{ fontSize: "13px", fontWeight: 600 }}>
                {label}
            </div>
            <div className="text-dark" style={{ fontFamily: "'Outfit', sans-serif", fontSize: "2rem", lineHeight: 1.1, fontWeight: 700 }}>
                {value}
            </div>
            {sub ? (
                <div className="mt-1" style={{ fontSize: "12px", color: subColor || "#64748b" }}>
                    {sub}
                </div>
            ) : null}
        </Col>
    );

    // Score prediction summary — matches the design (4 KPIs + message line).
    renderPrediction = (prediction, targetLabel) => (
        <div
            className="p-4 mb-4"
            style={{ borderRadius: "16px", border: "1px solid #f1f2f5", background: "#ffffff" }}
        >
            <h6 className="font-weight-bold text-dark mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Predicted Score ({targetLabel})
            </h6>
            <p className="text-muted mb-4" style={{ fontSize: "12px" }}>
                Projected {targetLabel} score based on your performance trajectory — a forecast, not a result.
            </p>
            {!prediction ? (
                <p className="text-muted mb-0" style={{ fontSize: "14px" }}>
                    Not enough full-length attempts yet to project a score.
                </p>
            ) : (
                <Row>
                    {this.renderMetric(
                        `Predicted ${(prediction.target || targetLabel || "").toUpperCase()} Score`,
                        <>
                            {prediction.predictedScore}
                            <small className="text-muted" style={{ fontSize: "1rem" }}>/{prediction.maxScore}</small>
                        </>,
                        <span>
                            <Badge variant={TREND_VARIANT[prediction.trend] || "secondary"} className="mr-1">
                                {prediction.trend}
                            </Badge>
                            <span className="text-muted">
                                {num(prediction.trajectorySlope) >= 0 ? "+" : ""}
                                {prediction.trajectorySlope} pts/test
                            </span>
                        </span>
                    )}
                    {this.renderMetric(
                        "Current Avg (scaled)",
                        <>
                            {prediction.currentAvgScore}
                            <small className="text-muted" style={{ fontSize: "1rem" }}>/{prediction.maxScore}</small>
                        </>,
                        `${prediction.examsAnalyzed} attempts analysed`,
                        "#4f46e5"
                    )}
                    {this.renderMetric(
                        `Target Cutoff (${prediction.cutoffScore})`,
                        <Badge
                            variant={prediction.meetsCutoff ? "success" : "danger"}
                            style={{ fontSize: "1rem", padding: "8px 14px" }}
                        >
                            {prediction.meetsCutoff ? "On Track" : "Below Cutoff"}
                        </Badge>
                    )}
                    {prediction.message ? (
                        <Col xs={12}>
                            <p className="text-muted mb-0 mt-2" style={{ fontSize: "13px" }}>
                                {prediction.message}
                            </p>
                        </Col>
                    ) : null}
                </Row>
            )}
        </div>
    );

    // ── MOU #6: Percentile Standing — within batch (section/branch), across
    // batches (platform), and the simulated All-India percentile. ─────────────
    renderPercentileStanding = (ps, prediction) => {
        if (!ps && !prediction) return null;
        const airPct = prediction && prediction.projectedPercentile != null ? num(prediction.projectedPercentile) : null;
        const cards = [
            { label: "Within Section", value: ps && ps.sectionPercentile != null ? ps.sectionPercentile : null, sub: ps && ps.sectionSize ? `of ${ps.sectionSize} in your section` : "your section", color: "#6366f1" },
            { label: "Within Branch", value: ps && ps.branchPercentile != null ? ps.branchPercentile : null, sub: ps && ps.branchSize ? `of ${ps.branchSize} in your branch` : "your branch", color: "#0ea5e9" },
            { label: "Across Batches", value: ps && ps.platformPercentile != null ? ps.platformPercentile : null, sub: "all students on the platform", color: "#a855f7" },
            { label: "All-India (simulated)", value: airPct, sub: "projected NEET/JEE percentile", color: "#f59e0b" },
        ];
        const pctColor = (v) => (v == null ? "#94a3b8" : v >= 90 ? "#16a34a" : v >= 70 ? "#0ea5e9" : v >= 40 ? "#d97706" : "#dc2626");
        return (
            <div className="p-4 mb-4" style={{ borderRadius: "16px", border: "1px solid #f1f2f5", background: "#ffffff" }}>
                <h6 className="font-weight-bold text-dark mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    Percentile Standing
                </h6>
                <p className="text-muted mb-4" style={{ fontSize: "12px" }}>
                    Where you stand within your section &amp; branch (batch), across all batches, and your simulated All-India percentile
                    {ps && ps.examsConsidered ? ` · ${ps.examsConsidered} exams considered` : ""}
                </p>
                <Row>
                    {cards.map((c) => (
                        <Col md={3} sm={6} key={c.label} className="mb-3 mb-md-0">
                            <div className="p-3 h-100" style={{ borderRadius: 14, border: "1px solid #f1f2f5", background: "#fafaff" }}>
                                <div className="text-muted mb-1" style={{ fontSize: 12.5, fontWeight: 600 }}>{c.label}</div>
                                <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: "1.9rem", fontWeight: 700, lineHeight: 1.1, color: pctColor(c.value) }}>
                                    {c.value != null ? `${c.value}` : "—"}
                                    {c.value != null ? <small className="text-muted" style={{ fontSize: "0.9rem", fontWeight: 600 }}> %ile</small> : null}
                                </div>
                                <div className="text-muted mt-1" style={{ fontSize: 11 }}>{c.sub}</div>
                            </div>
                        </Col>
                    ))}
                </Row>
            </div>
        );
    };
    // Subject-wise Marks Trend — one line per subject across periods.
    renderSubjectTrend = (subjectTrend) => {
        // Align all subjects onto a single, time-ordered category axis.
        const labelSet = [];
        subjectTrend.forEach((s) =>
            (s.points || []).forEach((p) => {
                if (!labelSet.includes(p.label)) labelSet.push(p.label);
            })
        );
        const colors = ["#077EE6", "#27AE60", "#F2994A", "#9B51E0", "#EB5757", "#2D9CDB"];
        const options = {
            chart: { type: "line", backgroundColor: "transparent", height: 300 },
            title: { text: "" },
            credits: { enabled: false },
            xAxis: {
                categories: labelSet,
                lineColor: "#e2e8f0",
                labels: { style: { color: "#64748b", fontWeight: "600" } },
            },
            yAxis: { title: { text: "Avg Subject Score" }, gridLineColor: "#eef2f6", labels: { style: { color: "#64748b" } } },
            tooltip: pointTooltip({}),
            legend: { align: "center", verticalAlign: "top" },
            series: subjectTrend.map((s, i) => {
                const byLabel = {};
                (s.points || []).forEach((p) => {
                    byLabel[p.label] = p.avgScore;
                });
                return {
                    name: s.subject,
                    color: colors[i % colors.length],
                    data: labelSet.map((l) => (byLabel[l] != null ? Number(byLabel[l]) : null)),
                };
            }),
        };
        const table = (
            <div className="table-responsive">
                {subjectTrend.map((s, si) => {
                    const points = s.points || [];
                    return (
                        <div key={s.subject_id ?? si} className={si > 0 ? "mt-3" : ""}>
                            <div className="font-weight-bold text-dark mb-1" style={{ fontSize: 13 }}>{s.subject}</div>
                            <table className="table table-borderless mb-0">
                                <thead>
                                    <tr style={{ borderBottom: "1px solid #eef2f6" }}>
                                        <th className="text-muted" style={{ fontSize: 11, textTransform: "uppercase", fontWeight: 700 }}>Period</th>
                                        <th className="text-muted text-right" style={{ fontSize: 11, textTransform: "uppercase", fontWeight: 700 }}>Avg Score</th>
                                        <th className="text-muted text-right" style={{ fontSize: 11, textTransform: "uppercase", fontWeight: 700 }}>Accuracy</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {points.map((p, pi) => (
                                        <tr key={pi} style={{ borderBottom: "1px solid #f6f8fb" }}>
                                            <td className="font-weight-bold text-dark" style={{ fontSize: 13 }}>{p.label || "-"}</td>
                                            <td className="text-right font-weight-bold" style={{ fontSize: 13 }}>{p.avgScore != null ? p.avgScore : "-"}</td>
                                            <td className="text-right text-muted" style={{ fontSize: 13 }}>{p.accuracy != null ? p.accuracy : "-"}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    );
                })}
            </div>
        );
        return this.renderChartCard("Subject-wise Marks Trend", subjectTrend.length > 0, options, "No subject data in this period.", "subjectTrendView", table);
    };

    // Shared chart card shell so both charts line up in the grid.
    // viewKey is the state field holding "graph" | "table"; tableContent is the
    // table element to render in table mode. When omitted, no toggle is shown.
    renderChartCard = (title, hasData, options, emptyText, viewKey, tableContent, badge) => {
        const view = viewKey ? this.state[viewKey] : "graph";
        return (
            <div
                className="p-4 h-100"
                style={{ borderRadius: "16px", border: "1px solid #f1f2f5", background: "#ffffff" }}
            >
                <div className="d-flex align-items-start justify-content-between mb-3" style={{ gap: 10 }}>
                    <div>
                        <h6 className="font-weight-bold text-dark mb-0" style={{ fontFamily: "'Outfit', sans-serif" }}>
                            {title}
                        </h6>
                        {badge || null}
                    </div>
                    {viewKey && hasData ? (
                        <div className="sp-segment sp-segment-sm">
                            <button type="button" className={view === "graph" ? "active" : ""} onClick={() => this.setState({ [viewKey]: "graph" })}>
                                <i className="fas fa-chart-column" />Graph
                            </button>
                            <button type="button" className={view === "table" ? "active" : ""} onClick={() => this.setState({ [viewKey]: "table" })}>
                                <i className="fas fa-table" />Table
                            </button>
                        </div>
                    ) : null}
                </div>
                {!hasData ? (
                    <div className="text-center text-muted py-4" style={{ fontSize: "14px" }}>
                        {emptyText}
                    </div>
                ) : view === "table" ? (
                    tableContent
                ) : (
                    <HighchartsReact highcharts={Highcharts} options={options} containerProps={{ style: { width: "100%" } }} />
                )}
            </div>
        );
    };

    render() {
        const { prediction, subjectTrend, loading, target } = this.state;
        const mobile = resolveMobile(this.props.student);
        const targetLabel = TARGETS.find((t) => t.value === target)?.label || target.toUpperCase();

        return (
            <TabSection
                icon="fa-trophy"
                title="Prediction"
                description="Predicted scores and percentile projections."
                headerRight={this.renderControls()}
            >
                {loading ? (
                    <PageShimmer />
                ) : !mobile ? (
                    <div className="text-center text-muted py-4" style={{ fontSize: "14px" }}>
                        No mobile number available for this student.
                    </div>
                ) : (
                    <>
                        {/* MOU #6 Percentile standing → #7 Prediction.
                            (Rank widgets removed &mdash; ranks are no longer shown on the profile.
                            Subject-wise Marks Trend lives on the Marks Trend tab, Req #1.) */}
                        {this.renderPercentileStanding(this.state.percentileStanding, prediction)}
                        {this.renderPrediction(prediction, targetLabel)}
                    </>
                )}
            </TabSection>
        );
    }
}

export default compose(withApollo, withRouter)(PredictionRankTab);
